// web/src/components/ds/DsToaster.tsx — DS.8 (sprint aos-ds-timeline-reskin,
// spec §2 D5/D6). The store -> `@ds/feedback/Toast` adapter that replaces
// `sonner` as the RENDERER. `sonner` is uninstalled; the store's public API is
// unchanged.
//
// Five things the adapter has to do that the vendored piece cannot:
//
//  (a) DISARM THE PIECE'S TIMER — `duration={0}`. `@ds/feedback/Toast` fires
//      ONE `onClose` for its internal `setTimeout`, its close ✕ and Escape
//      alike, and `ToastContainer` wires `onClose -> onRemove(id)`. With the
//      timer armed, `onRemove -> dismiss(id)` would fire the caller's
//      `onDismiss` on every 5-second expiry — i.e. `useSocket`'s notification
//      toast would mark the notification READ (persisted, broadcast to every
//      open window) just by being ignored. `duration={0}` makes `onRemove`
//      reachable ONLY from an explicit dismissal, which is what `dismiss(id)`
//      means. `toast.store.ts` owns the expiry timer and removes silently.
//      It also suppresses the piece's progress track, which is gated on
//      `duration > 0` — so no progress bar ships.
//
//  (b) SUPPLY THE ICON FAMILY. The piece draws its type icon through
//      `@ds/primitives/Icon`, whose renderer comes from React context. That
//      context is mounted by `DsPortalProvider`, and (c) below puts this
//      component OUTSIDE it — so the adapter mounts `IconFamilyLucide` itself.
//      Without this the toast renders the literal word "info".
//
//  (c) THE Z-TIER AND THE SAFE-AREA OFFSET, through a child combinator.
//      `ToastContainerProps` is `{ toasts, onRemove }` — no `className`, no
//      `position`, no `offset` [medido: ToastContainer at the pinned v0.2.0],
//      so the consumer cannot put a class on the fixed container. The rule
//      lives in `ds-adapters.css` as `.aos-toast-host > div { ... }`
//      (specificity 0,1,1, which beats the CSS-module class at 0,1,0 and
//      needs no hashed-name knowledge). It carries BOTH the `--z-toast` rung
//      and the two safe-area offsets `<Toaster/>` used to take as props, so
//      the two decisions cost one declaration block.
//      `position="top-right"` needs no replacement: the DS `.container` is
//      already `position: fixed; top: var(--space-4); right: var(--space-4)`.
//      Filed upstream as issues (b) and (d) instead of forked (DS-C4).
//
//  (d) STAY OUTSIDE `.aos-ds-root`. Neither sonner nor the DS `ToastContainer`
//      portals — both render in place, in the React tree where they are
//      mounted. `.aos-ds-root` carries `isolation: isolate` (index.css, DS.4
//      D4) and, since DS.7 D6, wraps the WHOLE route table in `App.tsx`. A
//      toaster mounted inside it is TRAPPED in that stacking context no matter
//      how large its z-index, and would paint under every modal. `App.tsx`
//      mounts this as a SIBLING of `<DsPortalProvider>` for exactly that
//      reason; `ZOrderScene.stories.tsx` mirrors the structure and
//      `ds8-z-order.spec.ts` hit-tests it in a running browser, because a
//      `getComputedStyle` z-index comparison across four stacking contexts is
//      precisely the check that lets an isolate trap pass review.
//
//  (e) PUBLISH THE STACK'S MEASURED CLEARANCE as `--aos-toast-stack-clearance`
//      on `:root`. `SpawnPreparingToast` is a second fixed element in the same
//      top-right corner and has to sit BELOW the stack. Its old offset was a
//      hardcoded `6rem` derived from the retired vendor's COLLAPSED stacking
//      (three toasts peeking 14 px each), an arithmetic that has no referent
//      here: the DS container is a plain flex column, so its height grows
//      LINEARLY with the number of toasts and with each toast's own wrapped
//      height. No constant can be right — measured in a running browser at
//      1440x900, the container is 0 px empty, 78 px with one toast and 258 px
//      with three `[medido: getBoundingClientRect on `.aos-toast-host > div` in
//      the ZOrderScene story, n=1, 2026-09-14]`, i.e. the old 6rem/96px cleared
//      one toast by 2 px and was 180 px short of three.
//      So the number is REPLACED BY A MEASUREMENT rather than re-tuned: a
//      ResizeObserver publishes the live container height plus one `--space-3`
//      gap, and the pill's `top` reads it. Exact for any count, any wrap, and
//      nothing to go stale. Published at `0px` when the stack is empty, so the
//      pill's un-offset position is unchanged.
//
// The `title` prop takes the store's single `message` string; the store has no
// `description` and no `type`, so every toast is the piece's `info` default —
// the same undifferentiated look the retired vendor rendered.

import { useEffect, useRef } from 'react';
import { ToastContainer } from '@ds/feedback/Toast';
import { useToastStore } from '../../stores/toast.store';
import { IconFamilyLucide } from './IconFamilyLucide';
import './ds-adapters.css';

/**
 * The DS `.container`'s own flex `gap`, re-added below the stack so the pill
 * clears the last toast by the same rhythm the stack uses internally.
 *
 * READ, never hardcoded (reviewer, 2026-09-14): a `const … = 12` is a raw
 * design value duplicating `--space-3` in a file the token gate scans but
 * cannot see (its classes match CSS/Tailwind forms, not bare JS numbers), and
 * it would silently drift the day the token moves. The container's own
 * computed gap is the truth; `--space-3` off the root is the fallback for the
 * (jsdom) case where no layout engine resolved it.
 */
function stackGapPx(container: HTMLElement): number {
  const own = Number.parseFloat(getComputedStyle(container).rowGap);
  if (Number.isFinite(own) && own > 0) return own;
  const token = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--space-3'),
  );
  return Number.isFinite(token) && token > 0 ? token : 0;
}

export const TOAST_STACK_CLEARANCE_VAR = '--aos-toast-stack-clearance';

export function DsToaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  const hostRef = useRef<HTMLDivElement>(null);

  // (e) — measure, never guess. Deps are EMPTY on purpose: the observer is
  // attached once to the vendor's container (which `ToastContainer` renders
  // unconditionally, empty or not) and fires on every size change, including
  // the ones a toast count change causes. Re-running it per render would
  // detach and re-attach an observer on every push.
  useEffect(() => {
    const container = hostRef.current?.firstElementChild;
    if (!(container instanceof HTMLElement)) return;
    const root = document.documentElement;
    const publish = () => {
      const h = container.getBoundingClientRect().height;
      root.style.setProperty(
        TOAST_STACK_CLEARANCE_VAR,
        h > 0 ? `${Math.round(h) + stackGapPx(container)}px` : '0px',
      );
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(publish);
    ro.observe(container);
    return () => {
      ro.disconnect();
      root.style.removeProperty(TOAST_STACK_CLEARANCE_VAR);
    };
  }, []);

  return (
    <div className="aos-toast-host" data-testid="ds-toaster-host" ref={hostRef}>
      <IconFamilyLucide>
        <ToastContainer
          toasts={toasts.map((t) => ({
            id: t.id,
            props: {
              title: t.message,
              action: t.action,
              // (a) — never let the piece close itself.
              duration: 0,
            },
          }))}
          onRemove={dismiss}
        />
      </IconFamilyLucide>
    </div>
  );
}

export default DsToaster;
